import { getNoteBech32Id, isProtectedEvent } from '@/lib/event'
import { tagNameEquals } from '@/lib/tag'
import { toShare } from '@/lib/link'
import { simplifyUrl } from '@/lib/url'
import { mutePubkeySetAtom, unwrappedPinListAtom, favoriteRelaysWithSetsAtom } from '@/atoms/lists'
import { currentPubkeyAtom } from '@/atoms/session'
import { clearPinBuryState, getPinBuryState, setBuried, setPinned } from '@/atoms/pinBury'
import client from '@/services/client.service'
import {
  ArrowDown,
  Pin,
  ArrowUp,
  PinOff,
  Copy,
  Link,
  Code,
  SatelliteDish,
  Alert,
  Bell,
  BellOff,
  FilePenIcon,
  CloudUpload,
  Trash2,
  ArrowUpRight01Icon,
  Add01Icon
} from '@hugeicons/core-free-icons'
import { HugeiconsIcon, type IconSvgElement } from '@hugeicons/react'
import { Event } from '@nostr/tools/wasm'
import * as kinds from '@nostr/tools/kinds'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import RelayIcon from '../RelayIcon'
import { discardPendingEvent, pendingIdsAtom, savePendingEvent } from '@/atoms/pending'
import { groupedAtom, recentRelayFeedsAtom } from '@/atoms/feed'
import { usePendingPublishMap } from '@/lib/pendingPublish'
import { atom, useAtomValue } from 'jotai'
import { unwrap } from 'jotai/utils'
import { ResolvedSet } from '@nostr/gadgets/sets'
import { pin, unpin } from '@/atoms/pin'
import { mutePrivately, mutePublicly, unmute } from '@/atoms/mutes'

export interface SubMenuAction {
  label: React.ReactNode
  onClick: () => void
  className?: string
  separator?: boolean
}

export interface MenuAction {
  icon: IconSvgElement
  label: string
  onClick?: () => void
  className?: string
  separator?: boolean
  subMenu?: SubMenuAction[]
  disabled?: boolean
  title?: string
}

interface UseMenuActionsProps {
  event: Event
  closeDrawer: () => void
  openEditor: () => void
  showSubMenuActions: (subMenu: SubMenuAction[], title: string) => void
  setIsRawEventDialogOpen: (open: boolean) => void
  setIsReportDialogOpen: (open: boolean) => void
  isSmallScreen: boolean
}

export type TBroadcastRelayItem = string | ResolvedSet<string> | { separator: true }

/**
 * Combined list of relays and relay sets available as republish targets.
 *
 * Sections separated by `{ separator: true }` markers:
 *   1. favorites the user has starred (kind:10012) + their relay sets
 *   2. window.hallway.universe.defaultFavoriteRelays
 *   3. window.hallway.universe.hardcodedFeeds
 *   4. last 5 relay feeds the user has visited (non-duplicate)
 *
 * Duplicates removed within each group; a relay that appears in multiple
 * groups only shows in the first group where it was added.
 */
const republishRelaysAtom = atom<Promise<TBroadcastRelayItem[]>>(async (get) => {
  const recent = get(recentRelayFeedsAtom)
  const favorites = await get(favoriteRelaysWithSetsAtom)
  const universe = window.hallway?.universe

  const result: TBroadcastRelayItem[] = []
  const seenRelays = new Set<string>()
  const seenSets = new Set<string>()

  const addRelay = (url: string) => {
    if (!url) return
    const key = url.replace(/\/+$/, '').toLowerCase()
    if (seenRelays.has(key)) return
    seenRelays.add(key)
    result.push(url)
  }
  const addSet = (relaySet: ResolvedSet<string>) => {
    const key = `${relaySet.pointer.pubkey}:${relaySet.pointer.identifier}`
    if (seenSets.has(key)) return
    seenSets.add(key)
    result.push(relaySet)
  }

  // Section 1: user favorites
  const favStart = result.length
  for (const f of favorites) {
    if (typeof f === 'string') addRelay(f)
    else addSet(f)
  }

  // Section 2: defaultFavoriteRelays
  const defaults = universe?.defaultFavoriteRelays ?? []
  if (defaults.length > 0 && result.length > favStart) result.push({ separator: true })
  for (const url of defaults) addRelay(url)

  // Section 3: hardcodedFeeds
  const hardcoded = universe?.hardcodedFeeds ?? []
  if (hardcoded.length > 0 && result.length > 0) result.push({ separator: true })
  for (const item of hardcoded) {
    if (item.relays) {
      for (const url of item.relays) addRelay(url)
    }
  }

  // Section 4: recent relay feeds visited
  if (recent.length > 0 && result.length > 0) result.push({ separator: true })
  for (const ref of recent) {
    if (typeof ref === 'string') addRelay(ref)
    else addSet(ref)
  }

  return result
})

const unwrappedRepublishRelaysAtom = unwrap(republishRelaysAtom)

export function useMenuActions({
  event,
  closeDrawer,
  openEditor,
  showSubMenuActions,
  setIsRawEventDialogOpen,
  setIsReportDialogOpen,
  isSmallScreen
}: UseMenuActionsProps) {
  const { t } = useTranslation()
  const pubkey = useAtomValue(currentPubkeyAtom)
  const mutePubkeySet = useAtomValue(mutePubkeySetAtom)
  const pinList = useAtomValue(unwrappedPinListAtom) ?? []
  const favorites = useAtomValue(unwrappedRepublishRelaysAtom)
  const grouped = useAtomValue(groupedAtom)
  const pendingIds = useAtomValue(pendingIdsAtom)
  const pendingPublishMap = usePendingPublishMap()
  const isMuted = useMemo(() => mutePubkeySet.has(event.pubkey), [mutePubkeySet, event])
  const pinBuryState = useMemo(() => getPinBuryState(event.pubkey), [event.pubkey])
  const isPending = useMemo(
    () => pendingIds.includes(event.id) || pendingPublishMap.has(event.id),
    [pendingIds, pendingPublishMap, event.id]
  )

  const broadcastSubMenu: SubMenuAction[] = useMemo(() => {
    const items: SubMenuAction[] = []

    if (pubkey && favorites) {
      let needSeparator = false
      for (let index = 0; index < favorites.length; index++) {
        const fav = favorites[index]

        if (typeof fav === 'object' && 'separator' in fav) {
          needSeparator = items.length > 0
          continue
        }

        if (typeof fav === 'string') {
          items.push({
            label: (
              <div className="flex items-center gap-2 w-full">
                <RelayIcon url={fav} />
                <div className="flex-1 truncate text-left">{simplifyUrl(fav)}</div>
              </div>
            ),
            onClick: async () => {
              closeDrawer()
              const promise = (async () => {
                await client.publishEvent([fav], event)
                discardPendingEvent(event.id)
              })()
              toast.promise(promise, {
                loading: t('Publishing...'),
                success: () => {
                  return t('Post successful')
                },
                error: (err) => {
                  return t('Failed to post') + ': ' + err.message
                }
              })
            },
            separator: needSeparator && items.length > 0
          })
          needSeparator = false
        } else {
          items.push({
            label: <div className="text-left truncate">{fav.title}</div>,
            onClick: async () => {
              closeDrawer()
              const promise = (async () => {
                await client.publishEvent(fav.items, event)
                discardPendingEvent(event.id)
              })()
              toast.promise(promise, {
                loading: t('Publishing...'),
                success: () => {
                  return t('Post successful')
                },
                error: (err) => {
                  return t('Failed to post') + ': ' + err.message
                }
              })
            },
            separator: needSeparator && items.length > 0
          })
          needSeparator = false
        }
      }
    }

    items.push({
      label: (
        <div className="flex items-center gap-2 w-full">
          <HugeiconsIcon icon={Add01Icon} />
          <div className="flex-1 text-left">{t('Custom relay URL')}</div>
        </div>
      ),
      onClick: () => {
        closeDrawer()
        const input = prompt(t('Enter relay URL (e.g. wss://relay.example.com)'))
        if (!input) return
        let url = input.trim()
        if (!/^wss?:\/\//i.test(url)) {
          url = 'wss://' + url
        }
        const promise = (async () => {
          await client.publishEvent([url], event)
          discardPendingEvent(event.id)
        })()
        toast.promise(promise, {
          loading: t('Publishing...'),
          success: () => t('Post successful'),
          error: (err: Error) => t('Failed to post') + ': ' + err.message
        })
      },
      separator: items.length > 0
    })

    return items
  }, [pubkey, favorites, event])

  const menuActions: MenuAction[] = useMemo(() => {
    const actions: MenuAction[] = []

    // Pin/Bury user actions (first block) - only when grouped notes is enabled
    if (grouped) {
      if (pinBuryState === 'pinned') {
        actions.push({
          icon: PinOff,
          label: t('Pin user'),
          onClick: () => {
            closeDrawer()
            clearPinBuryState(event.pubkey)
          }
        })
        actions.push({
          icon: ArrowDown,
          label: t('Bury user'),
          onClick: () => {
            closeDrawer()
            setBuried(event.pubkey)
          }
        })
      } else if (pinBuryState === 'buried') {
        actions.push({
          icon: ArrowUp,
          label: t('Unbury user'),
          onClick: () => {
            closeDrawer()
            clearPinBuryState(event.pubkey)
          }
        })
        actions.push({
          icon: Pin,
          label: t('Unpin user'),
          onClick: () => {
            closeDrawer()
            setPinned(event.pubkey)
          }
        })
      } else {
        actions.push({
          icon: Pin,
          label: t('Pin user'),
          onClick: () => {
            closeDrawer()
            setPinned(event.pubkey)
          }
        })
        actions.push({
          icon: ArrowDown,
          label: t('Bury user'),
          onClick: () => {
            closeDrawer()
            setBuried(event.pubkey)
          }
        })
      }
    }

    // Standard actions
    actions.push(
      ...([
        {
          icon: Copy,
          label: t('Copy event ID'),
          onClick: () => {
            navigator.clipboard.writeText(getNoteBech32Id(event))
            closeDrawer()
          },
          separator: grouped // Only add separator if pin/bury actions were shown
        },
        !isPending && {
          icon: Link,
          label: t('Copy share link'),
          onClick: () => {
            navigator.clipboard.writeText(toShare(getNoteBech32Id(event)))
            closeDrawer()
          }
        },
        ...(() => {
          const handlerRegistry = window.hallway.universe?.clientHandlers
          const fallbackHandlers = handlerRegistry?.fallback ?? []
          const kindHandlers = handlerRegistry?.byKind?.[event.kind] ?? []
          const supportedClients = [...kindHandlers, ...fallbackHandlers]
          if (supportedClients.length === 0) return []

          const subMenu = supportedClients.map((c) => {
            const href = c.urlPattern
              .replace(/\{\}/g, getNoteBech32Id(event))
              .replace(/\{e\}/g, event.id)
              .replace(/\{p\}/g, event.pubkey)
              .replace(/\{kind\}/g, String(event.kind))
              .replace(/\{d\}/g, event.tags.find(tagNameEquals('d'))?.[1] ?? '')
              .replace(/\{hint\}/g, client.getEventHint(event.id, event))
            return {
              label: c.name,
              onClick: () => {
                window.open(href, '_blank')
                closeDrawer()
              }
            }
          })

          return [
            {
              icon: ArrowUpRight01Icon,
              label: t('Open in another client'),
              subMenu,
              separator: true
            }
          ]
        })(),

        {
          icon: Code,
          label: t('View raw event'),
          onClick: () => {
            closeDrawer()
            setIsRawEventDialogOpen(true)
          },
          separator: true
        }
      ].filter(Boolean) as MenuAction[])
    )

    const isProtected = isProtectedEvent(event)
    if (!isProtected || event.pubkey === pubkey) {
      actions.push({
        icon: SatelliteDish,
        label: isPending ? t('Publish to ...') : t('Republish to ...'),
        onClick: isSmallScreen
          ? () =>
              showSubMenuActions(
                broadcastSubMenu,
                isPending ? t('Publish to ...') : t('Republish to ...')
              )
          : undefined,
        subMenu: broadcastSubMenu,
        separator: true
      })
    }

    if (event.pubkey === pubkey && event.kind === kinds.ShortTextNote) {
      const pinned = pinList.includes(event.id)
      actions.push({
        icon: pinned ? PinOff : Pin,
        label: pinned ? t('Unpin from profile') : t('Pin to profile'),
        onClick: async () => {
          closeDrawer()
          await (pinned ? unpin(event) : pin(event))
        }
      })
    }

    if (pubkey && event.pubkey !== pubkey) {
      actions.push({
        icon: Alert,
        label: t('Report'),
        className: 'text-destructive focus:text-destructive',
        onClick: () => {
          closeDrawer()
          setIsReportDialogOpen(true)
        },
        separator: true
      })
    }

    if (pubkey && event.pubkey !== pubkey) {
      if (isMuted) {
        actions.push({
          icon: Bell,
          label: t('Unmute user'),
          onClick: () => {
            closeDrawer()
            unmute(event.pubkey)
          },
          className: 'text-destructive focus:text-destructive',
          separator: true
        })
      } else {
        actions.push(
          {
            icon: BellOff,
            label: t('Mute user privately'),
            onClick: () => {
              if (!client.signer?.nip04Decrypt) return
              closeDrawer()
              mutePrivately(event.pubkey)
            },
            className: 'text-destructive focus:text-destructive',
            separator: true,
            disabled: !client.signer?.nip04Decrypt,
            title: !client.signer?.nip04Decrypt
              ? t('Your login method does not support encryption')
              : undefined
          },
          {
            icon: BellOff,
            label: t('Mute user publicly'),
            onClick: () => {
              closeDrawer()
              mutePublicly(event.pubkey)
            },
            className: 'text-destructive focus:text-destructive'
          }
        )
      }
    }

    if (pubkey && event.pubkey === pubkey) {
      if (isPending) {
        actions.push({
          icon: FilePenIcon,
          label: t('Edit'),
          onClick: () => {
            closeDrawer()
            openEditor()
          },
          className: 'text-black focus:text-black dark:text-black dark:focus:text-black',
          separator: true
        })
      }

      actions.push({
        icon: isPending ? CloudUpload : Trash2,
        label: isPending ? t('Publish this note') : t('Try deleting this note'),
        onClick: async () => {
          closeDrawer()
          if (isPending) {
            const promise = (async () => {
              const relays = await client.determineTargetRelays(event)
              await client.publishEvent(relays, event)
              discardPendingEvent(event.id)
            })()
            toast.promise(promise, {
              loading: t('Publishing...'),
              success: () => t('Post successful'),
              error: (err) => {
                savePendingEvent(event)
                return t('Failed to post') + ': ' + err.message
              }
            })
          } else {
            client.attemptDelete(event)
          }
        },
        className: 'text-destructive focus:text-destructive',
        separator: true
      })
    }

    return actions
  }, [
    t,
    event,
    pubkey,
    isMuted,
    isPending,
    isSmallScreen,
    broadcastSubMenu,
    pinList,
    pinBuryState,
    grouped,
    closeDrawer,
    openEditor,
    showSubMenuActions,
    setIsRawEventDialogOpen,
    mutePrivately,
    mutePublicly,
    unmute,
    setPinned,
    setBuried,
    clearPinBuryState
  ])

  return menuActions
}
